import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Loader2, ShieldAlert } from 'lucide-react';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', dob: '' });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Compute max DOB (must be at least 18 today) for client-side date picker.
  const today = new Date();
  const maxDob = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    .toISOString().split('T')[0];
  const minDob = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())
    .toISOString().split('T')[0];

  const computeAge = (dobStr) => {
    if (!dobStr) return null;
    const d = new Date(dobStr);
    if (Number.isNaN(d.getTime())) return null;
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error('Please agree to the Terms of Service to continue.');
      return;
    }
    const age = computeAge(formData.dob);
    if (age === null) {
      toast.error('Please enter your date of birth.');
      return;
    }
    if (age < 18) {
      toast.error('You must be 18 or older to join Bookish Bestie.');
      return;
    }
    setLoading(true);

    const result = await register(formData.name, formData.email, formData.password, formData.dob);
    setLoading(false);

    if (result.success) {
      toast.success('Account created! One quick step to unlock everything.');
      navigate('/checkout');
    } else {
      toast.error(result.error || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6 paper-texture">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BookOpen className="w-8 h-8 text-[#6B7A4F]" />
            <span className="text-2xl font-bold text-[#2D2A26]">Bookish Bestie</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26] mb-2">
            Start Your <span className="font-bold">Journey</span>
          </h1>
          <p className="text-[#5C5955]">Create an account to find your bookish bestie</p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B7A4F] mt-3 opacity-70">
            A Built for Bookish Experience
          </p>
        </div>

        <div className="glass-card p-8 rounded-2xl space-y-6">
          <form data-testid={AUTH.registerForm} onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#2D2A26] font-medium">
                Name
              </Label>
              <Input
                id="name"
                data-testid={AUTH.registerNameInput}
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2D2A26] font-medium">
                Email
              </Label>
              <Input
                id="email"
                data-testid={AUTH.registerEmailInput}
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#2D2A26] font-medium">
                Password
              </Label>
              <Input
                id="password"
                data-testid={AUTH.registerPasswordInput}
                type="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dob" className="text-[#2D2A26] font-medium">
                Date of Birth <span className="text-[#6B7A4F]">*</span>
              </Label>
              <Input
                id="dob"
                data-testid="register-dob-input"
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                max={maxDob}
                min={minDob}
                required
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
              <p className="text-xs text-[#5C5955]">
                You must be <strong>18 or older</strong>. We use your birth date to match you with readers within an 8-year age range.
              </p>
            </div>

            {/* Terms of Service summary + required agreement */}
            <div className="bg-[#E2E6D5]/40 border border-[#6B7A4F]/40 rounded-2xl p-4 space-y-3">
              <div className="flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-[#6B7A4F] flex-shrink-0 mt-0.5" />
                <p className="text-xs font-bold tracking-wider uppercase text-[#6B7A4F]">
                  Before you continue — please read
                </p>
              </div>
              <ul className="text-[13px] text-[#2D2A26] leading-snug space-y-2 list-disc pl-5">
                <li>
                  <strong>$10 one-time payment</strong> unlocks access. Not a subscription — you'll never be billed again.
                </li>
                <li>
                  <strong>All sales are final.</strong> No refunds, ever, for any reason.
                </li>
                <li>
                  Access lasts <strong>for as long as the Service is operational</strong>. If Bookish Bestie is ever shut down, discontinued, or unavailable, you are <strong>not entitled to any refund, credit, or compensation</strong>.
                </li>
                <li>
                  You meet other users in real life at your own risk. Bookish Bestie is not liable for your interactions.
                </li>
              </ul>
              <label className="flex items-start gap-3 pt-2 cursor-pointer group" data-testid="register-terms-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="register-terms-checkbox"
                  className="mt-1 w-4 h-4 accent-[#6B7A4F] flex-shrink-0"
                  required
                />
                <span className="text-sm text-[#2D2A26] leading-snug">
                  I have read and agree to the{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6B7A4F] font-semibold underline hover:no-underline"
                    data-testid="register-terms-link"
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6B7A4F] font-semibold underline hover:no-underline"
                  >
                    Privacy Policy
                  </Link>
                  , including the no-refund and service-discontinuation clauses above.
                </span>
              </label>
            </div>

            <Button
              data-testid={AUTH.registerSubmitBtn}
              type="submit"
              disabled={loading || !agreed}
              className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white py-6 rounded-full text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-[#5C5955]">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-[#6B7A4F] font-semibold hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-[#5C5955] hover:text-[#6B7A4F] transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
